import React from 'react';
import cn from 'classnames';
import {
  FeatureIcon,
  Link,
  MediaContent,
  StickyContent,
  SubNavigationItem,
} from '~/common/models';
import { Heading } from './components/heading';
import { Navigation } from './components/navigation';
import { PageHero, FeatureContentIcons } from 'common/components';
import { ButtonTypeEnum } from '~/common/components/ui-elements';
import { HeaderBannerMediaContent } from './components/media-content';
import { StickyBlueRibbon } from './components/sticky-blue-ribbon';
import styles from './index.module.scss';

interface HeaderBannerProp {
  contentId: string;
  headerHeight: number;
  legalDisclaimer: string;
  legalDisclaimerFontColor: string;
  legalDisclaimerAlignment: 'auto' | 'right' | 'left' | 'center';
  bannerHyperlink?: Link;
  featureContents?: FeatureIcon[];
  mediaContents: MediaContent[];
  navigationItems: SubNavigationItem[];
  navigationFontColor: string;
  navigationBackgroundColor: string;
  navigationIsSticky: boolean;
  ctaButton: Link;
  ctaButtonStyle: keyof typeof ButtonTypeEnum;
  blueRibbon?: StickyContent;
}

export default (props: HeaderBannerProp) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [headingContent, setHeadingContent] = React.useState<
    FeatureIcon | undefined
  >(undefined);

  React.useEffect(() => {
    if (!!props.featureContents) {
      setHeadingContent(
        props.featureContents.find((f) => f.contentType === 'Heading')
      );
    }
  }, []);

  const isMobile = () => {
    if (containerRef.current === null) return false;
    return containerRef.current?.clientWidth < 923;
  };

  return (
    <>
      <div
        id="header-banner"
        // .full-device-width breaks out of the .customContentPage > .umb-block-grid
        // 1200px wrapper (blockgrid.css) so the banner spans the viewport on pages
        // like /great-australian-detour while text-only CustomContentPage pages
        // (/fleet) still get the centred 1200px layout.
        className={cn('full-device-width', styles.headerBannerMain)}
      >
        {props.blueRibbon && <StickyBlueRibbon content={props.blueRibbon} />}
        <PageHero
          style={{
            height: `${props.headerHeight}vh`,
          }}
          contentClass={styles.headerBanner}
          mediaContent={
            props.mediaContents?.some((mc) => !!mc.mediaSource)
              ? () => (
                  <HeaderBannerMediaContent
                    legalDisclaimer={props.legalDisclaimer}
                    legalDisclaimerColor={props.legalDisclaimerFontColor}
                    legalDisclaimerPosition={props.legalDisclaimerAlignment}
                    mediaContents={props.mediaContents}
                    bannerHyperlink={props.bannerHyperlink}
                  />
                )
              : undefined
          }
        >
          <div ref={containerRef} className={styles.headerBannerContent}>
            <FeatureContentIcons features={props.featureContents || []} />
          </div>
        </PageHero>

        {headingContent && <Heading heading={headingContent} />}
      </div>

      {!!props.navigationItems && props.navigationItems.length > 0 && (
        <Navigation
          items={props.navigationItems}
          navigationSticky={props.navigationIsSticky}
          backgroundColor={props.navigationBackgroundColor}
          color={props.navigationFontColor}
          ctaButton={props.ctaButton}
          ctaButtonStyle={props.ctaButtonStyle}
        />
      )}
    </>
  );
};
